import { useModelCheck } from "@/shared/api/models";
import { useAgents } from "@/shared/api/agents";
import { toast } from "sonner";
import { selectionOf } from "@/shared/lib/models";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Plus, ChevronDown, PanelRight, Pencil, Quote, ArrowDown, ArrowUp, GitBranch as GitBranchBase
} from "lucide-react";
import type { LucideProps } from "lucide-react";
import PixelAvatar from "@/shared/components/PixelAvatar";
import Logo from "@/shared/components/Logo";
import HeroGlow from "@/shared/components/HeroGlow";
import SummaryDivider from "@/shared/components/SummaryDivider";
import TranscriptDivider from "@/shared/components/TranscriptDivider";
import AgentTurn from "./AgentTurn";
import { chatSources, withSourceList } from "./chatSources";
import type { AppRefs } from "./RunTimeline";
import ChannelNote from "./ChannelNote";
import ComposerDock from "./ComposerDock";
import Composer, { MentionChip } from "./Composer";
import AgentContextPicker from "./AgentContextPicker";
import { openingFor } from "./starters";
import ArtifactViewer from "./ArtifactViewer";
import ChatMenu, { RenameInput, ChatPin } from "./ChatMenu";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { catalogFor } from "@/shared/lib/interface-catalog";
import {
  MessageBody, AttachmentRow, MessageActions, messageText, ErrorBubble, AudioBlock, RunStopped
} from "./MessageBlocks";
import { agentMood } from "@/shared/lib/agent-mood";
import useServerChat from "./hooks/useServerChat";
import useContextFollow from "./hooks/useContextFollow";
import useAgentSwitches from "./hooks/useAgentSwitches";
import useChatDraft from "./hooks/useChatDraft";
import { chatDraftKey } from "./drafts";
import { freePreviews } from "@/shared/lib/drafts";
import { useConnections } from "@/shared/api/apps";
import { useViewport } from "@/shared/hooks/useViewport";
import BottomSheet from "@/shared/components/responsive/BottomSheet";
import { fileKind, fileSize } from "@/shared/lib/files";
import type { Agent, Artifact, Attachment, Chat, Mention, Message, Vote, WebSource } from "@/shared/types";
import type { BuilderStart, ContextPick, LiveTurn, OlderMessages } from "./types";

/* lucide's props do not carry `title`; the branch marks below pass one and it
   reaches the DOM as an inert svg attribute. Typed so the freeze holds — see
   the delivery notes, this is a call-site defect, not a typing one. */
const GitBranch = GitBranchBase as React.ComponentType<LucideProps & { title?: string }>;

/** `onEdit` absent: the message cannot be resent from here (a channel conversation). */
function UserMessage({ m, onEdit }: { m: Message; onEdit?: (id: string, text: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(m.text || "");

  // Slash commands render as a chip — same visual system as mention chips.
  if (m.text?.startsWith("/")) {
    return (
      <div className="max-w-[85%] min-w-0">
        <MentionChip m={{ type: "command" as const, name: m.text }} />
      </div>
    );
  }

  if (editing) {
    return (
      <div className="w-full max-w-[85%] min-w-0">
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={Math.min(6, Math.max(2, draft.split("\n").length))}
          className="w-full resize-none rounded-[16px] bg-[var(--surface-2)] px-3.5 py-[9px] text-[14px] leading-[1.5] outline-none ring-1 ring-[var(--hairline-focus)]"
        />
        <div className="flex justify-end gap-1.5 mt-1.5">
          <button onClick={() => setEditing(false)} className="press h-7 px-3 rounded-full text-[12px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]">
            Cancel
          </button>
          <button
            onClick={() => { setEditing(false); if (draft.trim() && draft !== m.text) onEdit?.(m.id, draft.trim()); }}
            className="press h-7 px-3 rounded-full text-[12px] bg-[var(--brand)] text-[var(--brand-foreground)] hover:bg-[var(--brand-hover)]"
          >
            Save & resend
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[85%] min-w-0 flex flex-col items-end gap-2 group/user">
      {/* `!` on each chain result, leaving the `?.` short-circuits intact. */}
      {m.attachments?.length! > 0 && <AttachmentRow files={m.attachments!} />}
      {m.mentions?.length! > 0 && (
        <div className="flex flex-wrap justify-end gap-1.5">
          {m.mentions!.map((mm) => <MentionChip key={mm.id} m={mm} />)}
        </div>
      )}
      {m.text && (
        <div className="flex items-start gap-1.5 min-w-0">
          {onEdit && (
            <button
              onClick={() => { setDraft(m.text!); setEditing(true); }}
              className="press mt-2 w-6 h-6 shrink-0 rounded-[6px] opacity-0 group-hover/user:opacity-100 hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
              title="Edit & resend"
            >
              <Pencil className="w-3 h-3" strokeWidth={1.75} />
            </button>
          )}
          <div className="rounded-[16px] bg-[var(--surface-2)] text-[var(--text-primary)] px-3.5 py-[9px] text-[14px] leading-[1.5] whitespace-pre-wrap break-words min-w-0">
            {m.text}
            {m.edited && <span className="ml-1.5 text-[10px] text-[var(--text-quaternary)]">(edited)</span>}
          </div>
        </div>
      )}
      {m.audio && <AudioBlock duration={m.audio.duration} label={m.audio.label} />}
    </div>
  );
}

/** Within this of the bottom, the list counts as following it. */
const FOLLOW_SLACK_PX = 64;

/** Within this of the top, the page before the oldest message is read. */
const LOAD_OLDER_PX = 600;

/* The four write actions are optional: a transcript the dashboard only reads
   (a channel conversation) is handed none of them, and offers none. A vote is not one:
   it says what the reader thought of an answer, on the run's trace, and changes nothing
   in the conversation — so a channel conversation's answers can be rated too.

   The list follows the bottom while the reader is there: a resize observer keeps it
   pinned as a turn grows, before the frame is painted. Scrolled up to read, it stays
   put, with a button back down; a message of their own brings them back. A long chat opens
   on its newest messages, and scrolling near the top reads the page before them, put in
   above without moving what is being read. */
function MessageList({
  agent, messages, older, onOpenArtifact, activeArtifactId, onRetry, onEditMessage, onQuote, turn = null,
  onRegenerate, regenerable = null, onVote, branchedFrom, summaryEndId, onOpenSource, header = null, footer = null,
}: {
  /** Builder's (`definable`): a line marks each message about another agent than the last. */
  agent: Agent;
  messages: Message[];
  /** The messages before the first of `messages`, when the chat has more than it read. */
  older?: OlderMessages;
  onOpenArtifact?: (a: Artifact) => void;
  activeArtifactId?: string;
  onRetry?: (id: string) => void;
  onEditMessage?: (id: string, text: string) => void;
  onQuote?: (text: string) => void;
  /** The turn in flight, which `messages` ends with. */
  turn?: LiveTurn | null;
  onRegenerate?: (id: string, repeatActions?: boolean) => Promise<string[] | null>;
  /** The message Regenerate goes under (the newest answer, with nothing going); null for none. */
  regenerable?: string | null;
  onVote?: (answerId: string, value: Vote | null) => void;
  branchedFrom?: Chat["branchedFrom"];
  /** The message the agent's summary ends with: "Earlier messages summarised" goes after it. */
  summaryEndId?: string;
  onOpenSource?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const followBottom = useRef(true);
  const [away, setAway] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [selPop, setSelPop] = useState<{ text: string; x: number; y: number } | null>(null);
  // An app tool's row is drawn with the app's name and logo, from this agent's connections.
  const { data: connections } = useConnections(agent.id);
  const apps: AppRefs = useMemo(() => Object.fromEntries((connections ?? []).map((c) => [c.app.slug, c.app])), [connections]);
  // the last render's maps, so a turn whose sources didn't change keeps its own (`chatSources`)
  const previousSources = useRef(new Map<string, ReadonlyMap<number, WebSource>>());
  const sources = useMemo(() => chatSources(messages, previousSources.current), [messages]);
  useEffect(() => { previousSources.current = sources; }, [sources]);
  const switches = useAgentSwitches(agent.definable ? messages : []);

  const toBottom = (smooth = false) => {
    followBottom.current = true;
    setAway(false);
    const el = scrollRef.current;
    if (smooth) el?.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    else if (el) el.scrollTop = el.scrollHeight;
  };

  // A message the reader sent (or another chat opened) brings the bottom back.
  const asked = messages.findLast((m) => m.role === "user");
  useLayoutEffect(() => toBottom(), [asked?.id]);

  // Older messages went in above: the ones being read stay where they were. `contentHeight` is
  // the messages' height before they came, kept by the observer below as the newest turn grows —
  // theirs, not the list's: the button back down takes room in the list too. The browser's own
  // scroll anchoring is off (`overflow-anchor`), so the move is made once, here.
  const contentHeight = useRef(0);
  const oldest = messages[0]?.id;
  useLayoutEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    if (!followBottom.current && contentHeight.current) el.scrollTop += content.offsetHeight - contentHeight.current;
    contentHeight.current = content.offsetHeight;
  }, [oldest]);

  /** Near the top, the page before is read — unless one is on its way. */
  const loadNearTop = () => {
    const el = scrollRef.current;
    if (el && el.scrollTop < LOAD_OLDER_PX && older?.has && !older.loading) older.load();
  };
  // Asked again once a page or a re-read lands, for a reader up there: a load asked for while
  // the transcript was being read again is skipped, and one already at the top scrolls no more.
  useEffect(() => {
    if (!followBottom.current) loadNearTop();
  }, [messages, older?.has, older?.loading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const scroll = scrollRef.current;
    const content = contentRef.current;
    if (!scroll || !content) return;
    const observer = new ResizeObserver(() => {
      if (followBottom.current) scroll.scrollTop = scroll.scrollHeight;
      contentHeight.current = content.offsetHeight;
    });
    observer.observe(content);
    observer.observe(scroll);
    return () => observer.disconnect();
  }, []);

  const onMouseUp = () => {
    const sel = window.getSelection();
    const text = sel?.toString().trim();
    // `!`: `text` came off the same selection, so there is one.
    if (onQuote && text && text.length > 3 && scrollRef.current?.contains(sel!.anchorNode?.parentElement || sel!.anchorNode)) {
      const rect = sel!.getRangeAt(0).getBoundingClientRect();
      setSelPop({ text, x: rect.left + rect.width / 2, y: rect.top });
    } else {
      setSelPop(null);
    }
  };

  return (
    <div
      ref={scrollRef}
      onMouseUp={onMouseUp}
      onScroll={(e) => {
        const el = e.currentTarget;
        followBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < FOLLOW_SLACK_PX;
        setAway(!followBottom.current);
        setSelPop(null);
        loadNearTop();
      }}
      role="region"
      aria-label="Messages"
      className="flex-1 overflow-y-auto overflow-x-hidden [overflow-anchor:none] relative"
    >
      {selPop && (
        <button
          style={{ position: "fixed", left: selPop.x, top: selPop.y - 40, transform: "translateX(-50%)", zIndex: 50 }}
          className="press fade-in flex items-center gap-1.5 h-8 px-3 rounded-full bg-[var(--brand)] text-[var(--brand-foreground)] text-[12px] shadow-[0_8px_24px_-8px_rgba(0,0,0,0.35)]"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            onQuote?.(selPop.text);
            setSelPop(null);
            window.getSelection()?.removeAllRanges();
          }}
        >
          <Quote className="w-3 h-3" strokeWidth={1.75} />
          Ask about this
        </button>
      )}
      <div
        ref={contentRef}
        className="max-w-[720px] mx-auto px-4 py-6 md:px-6 md:py-8 space-y-6 min-w-0"
      >
        {header}
        {older?.has && (
          <TranscriptDivider icon={ArrowUp}>
            <button
              onClick={older.load}
              disabled={older.loading}
              className="press font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-60"
            >
              {older.loading ? "Loading earlier messages…" : "Earlier messages"}
            </button>
          </TranscriptDivider>
        )}
        {messages.map((m) => (
          <React.Fragment key={m.id}>
            {switches.get(m.id)}
            <div className={`min-w-0 ${m.role === "user" ? "flex justify-end" : "flex gap-3.5"}`}>
              {m.role === "agent" && (
                <div className="pt-[3px] shrink-0"><PixelAvatar seed={agent.seed} hue={agent.hue} emotion={agentMood(agent)} size={22} ring rounded={6} animate={turn?.id === m.id ? "run" : undefined} /></div>
              )}
              {m.role === "user" ? (
                <UserMessage m={m} onEdit={onEditMessage} />
              ) : m.error ? (
                <div className="flex-1 min-w-0">
                  <ErrorBubble error={m.error} onRetry={onRetry && (() => onRetry(m.id))} />
                </div>
              ) : (
                <div className="flex-1 min-w-0 group/msg">
                  {m.parts ? (
                    <AgentTurn
                      parts={m.parts}
                      live={turn?.id === m.id ? turn : null}
                      apps={apps}
                      agentName={agent.name}
                      durationMs={m.durationMs}
                      sources={sources.get(m.id)}
                    />
                  ) : (
                    <MessageBody
                      blocks={m.blocks}
                      text={m.text}
                      onOpenArtifact={onOpenArtifact}
                      activeArtifactId={activeArtifactId}
                    />
                  )}
                  {m.modelLabel && <p className="mt-2 text-[10.5px] text-[var(--text-tertiary)]">{m.modelLabel} · {m.modelSelection?.source === "connection" ? "Model usage billed by your provider" : "Billed by Definable"}</p>}
                  <MessageActions
                    text={() => withSourceList(messageText(m), sources.get(m.id) ?? new Map())}
                    vote={m.vote}
                    onVote={onVote && m.answerId ? (value) => onVote(m.answerId!, value) : undefined}
                    onRegenerate={onRegenerate && m.id === regenerable ? (repeat) => onRegenerate(m.id, repeat) : undefined}
                  />
                </div>
              )}
            </div>
            {m.id === summaryEndId && <SummaryDivider />}
            {/* Branch point — everything above was copied from the source chat */}
            {branchedFrom && m.id === branchedFrom.atMessageId && (
              <TranscriptDivider icon={GitBranch}>
                Branched from
                <button
                  onClick={onOpenSource}
                  className="press font-medium text-[var(--text-secondary)] underline underline-offset-2 decoration-[var(--hairline-strong)] hover:text-[var(--text-primary)] hover:decoration-[var(--text-tertiary)]"
                >
                  {branchedFrom.title}
                </button>
              </TranscriptDivider>
            )}
          </React.Fragment>
        ))}
        {footer}
      </div>
      {away && (
        <div className="sticky bottom-3 flex justify-center pointer-events-none">
          <button
            onClick={() => toBottom(true)}
            title="Jump to latest"
            className="press pointer-events-auto w-8 h-8 rounded-full flex items-center justify-center bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-lift text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] fade-in"
          >
            <ArrowDown className="w-[14px] h-[14px]" strokeWidth={2} />
          </button>
        </div>
      )}
    </div>
  );
}

/** Right-slot panel: the artifact viewer. */
type Panel = { type: "artifact"; artifact: Artifact };

export default function ChatPane({
  agent, chat, configSummary, composerNote, history, historyOpen = false, onHistoryOpenChange, onChatStarted,
  onRenameChat, onTogglePinChat, onBranchChat, onArchiveChat, onDeleteChat, onOpenChat,
  startWith, onStartedWith, onNewAgent,
}: {
  agent?: Agent | null;
  chat?: Chat | null;
  /** An agent's composer strip: its setup box and the chat's knowledge (`ConfigSummary`,
      `ChatKnowledge`). Builder's strip is its agent picker instead. */
  configSummary?: React.ReactNode;
  /** Tucked on top of the composer: the agent's unpublished changes (`UnpublishedNote`). */
  composerNote?: React.ReactNode;
  history?: React.ReactNode;
  historyOpen?: boolean;
  onHistoryOpenChange?: (open: boolean) => void;
  /** A first send on a new chat created this one on the server — open it. */
  onChatStarted?: (chatId: string) => void;
  onRenameChat: (title: string) => void;
  onTogglePinChat: () => void;
  onBranchChat: () => void;
  onArchiveChat: () => void;
  onDeleteChat: () => void;
  onOpenChat?: (chatId: string) => void;
  /** How this new, empty Builder chat opens (`BuilderStart`). */
  startWith?: BuilderStart | null;
  /** `startWith` was taken: it is not taken again. */
  onStartedWith?: () => void;
  /** + → New agent: a new Builder chat, handed this composer's draft. */
  onNewAgent?: (draft: string) => void;
}) {
  const [renamingTitle, setRenamingTitle] = useState(false);
  const modelProblem = useModelCheck();
  // Builder's picker: the agent each message is about (sent with it), a new one, or none
  const [pick, setPick] = useState<ContextPick>(startWith?.pick ?? null);
  const about = agent?.definable && pick?.kind === "agent" ? pick.agent : null;
  const { data: workspaceAgents, isFetching: agentsFetching } = useAgents();
  // Right-slot panel: the artifact viewer.
  const [panel, setPanel] = useState<Panel | null>(null);
  // what the composer holds and hasn't sent, kept per conversation while the person is elsewhere
  const draftKey = chatDraftKey(agent?.id ?? "", chat?.id);
  const draft = useChatDraft(draftKey, chat ? undefined : startWith?.draft);
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const { isDesktop } = useViewport();
  // the conversation a send from here started is this chat's own: the picker keeps its pick there
  const ownChat = useRef<string | null>(null);
  const source = useServerChat(agent?.id ?? null, chat ?? null, (threadId) => {
    // the new chat got its id: what's in the composer goes with it
    if (agent) draft.carry(draftKey, chatDraftKey(agent.id, threadId));
    ownChat.current = threadId;
    onChatStarted?.(threadId);
  }, about);
  useContextFollow({
    on: !!agent?.definable, chatId: chat?.id, ownChat, read: source.read, messages: source.messages,
    agents: workspaceAgents, agentsFetching, pick, setPick,
  });
  // the composer's draft and the picker took theirs as this chat mounted
  const startedWith = useRef(false);
  useEffect(() => {
    if (!startWith || startedWith.current || chat || !agent) return;
    startedWith.current = true;
    if (startWith.send) source.send({ id: `u-${Date.now()}`, role: "user", text: startWith.send });
    onStartedWith?.();
  }, [startWith, chat, agent, source, onStartedWith]);

  const chatId = chat?.id;
  useEffect(() => {
    setPanel(null);
  }, [chatId]);

  if (!agent) return null;

  const messages = source.messages;
  /* A conversation that came in through Telegram, Slack or WhatsApp is answered
     there, so nothing on this screen writes to it: no composer, no resend,
     retry or continue, no files. An empty one is still that conversation, not
     a new chat to begin. */
  const channel = chat?.via;
  const isEmpty = !channel && messages.length === 0;

  const openArtifact = (a: Artifact) => setPanel({ type: "artifact" as const, artifact: a });

  const modelReady = () => {
    // Definable runs on the platform's key: there is no model of the person's to check
    if (agent.definable) return true;
    const problem = modelProblem(selectionOf(agent));
    if (problem) { toast.error(problem); return false; }
    return true;
  };

  /** `fromBox`: the words are the composer's (a starter's are not, and leave the box as it is).
      The quote and files always go with them. A refused send hands it all back (`settle`). */
  const send = (text: string, mentions: Mention[] = [], fromBox = true) => {
    if (!modelReady()) return; // nothing was taken: the composer keeps it
    const taken = draft.take((now) => ({
      sent: { text, mentions, quote: now.quote, files: now.files },
      left: { ...now, quote: null, files: [], ...(fromBox ? { text: "", mentions: [] } : {}) },
    }));
    const { quote, files } = taken.draft;
    void source.send({
      id: `u-${Date.now()}`,
      role: "user",
      text: quote ? `> ${quote}\n\n${text}` : text,
      attachments: files.length ? files : undefined,
      mentions: mentions.length ? mentions : undefined,
    }).then((sent) => draft.settle(taken, sent));
  };

  const addFiles = (fileList: FileList | null) => {
    const arr = Array.from(fileList || []).map((f) => ({
      file: f,
      name: f.name,
      size: fileSize(f.size),
      kind: f.type?.startsWith("image/") ? "image" : fileKind(f.name),
      previewSrc: f.type?.startsWith("image/") ? URL.createObjectURL(f) : undefined,
    }));
    if (arr.length) draft.setFiles((now) => [...now, ...arr]);
  };
  // a file taken off before it was sent: its preview goes too
  const removeFile = (i: number) => {
    freePreviews(draft.files.slice(i, i + 1));
    draft.setFiles((now) => now.filter((_, j) => j !== i));
  };



  // New agent: in a new Builder chat the picker says so; anywhere else a new Builder chat
  // opens with the draft, so each new agent starts its own conversation
  const newAgent = (text: string) => {
    if (agent.definable && !chat) setPick({ kind: "new" });
    else onNewAgent?.(text);
  };
  const strip = agent.definable
    ? <AgentContextPicker value={pick} onChange={setPick} onNewAgent={() => newAgent(draft.text.trim())} />
    : configSummary;
  const { starters, placeholder } = openingFor(!!agent.definable, pick);
  const composerExtras = {
    strip,
    placeholder,
    onNewAgent: onNewAgent && newAgent,
    newAgentHint: agent.definable && chat ? "opens a new chat" : "with Builder",
    text: draft.text,
    onTextChange: draft.setText,
    mentions: draft.mentions,
    onMentionsChange: draft.setMentions,
    attachments: draft.files,
    onAddFiles: addFiles,
    onRemoveAttachment: removeFile,
    quote: draft.quote,
    onClearQuote: () => draft.setQuote(null),
  };

  /* Two cards in the content tray: the chat, and beside it on desktop the Chats
     history as its own card — same corners and outline, the tray's 4px between. */
  return (
    <div className="flex-1 min-w-0 min-h-0 flex gap-1">
    <main
      className="flex-1 min-w-0 flex flex-col bg-[var(--canvas)] overflow-hidden relative rounded-[18px] ring-1 ring-[var(--hairline)] shadow-lift"
      onDragEnter={(e) => { e.preventDefault(); dragDepth.current++; setDragging(true); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => { dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); }}
      onDrop={(e) => {
        e.preventDefault();
        dragDepth.current = 0;
        setDragging(false);
        // Still caught on a channel conversation — uncaught, the browser opens the file in place of the app.
        if (!channel) addFiles(e.dataTransfer.files);
      }}
    >
      {dragging && !channel && (
        <div className="absolute inset-0 z-40 bg-[var(--surface-glass)] backdrop-blur-[2px] flex items-center justify-center pointer-events-none fade-in">
          <div className="rounded-[18px] border-2 border-dashed border-[var(--hairline-focus)] bg-[var(--surface)] px-10 py-8 text-center shadow-[0_16px_48px_-16px_rgba(0,0,0,0.18)]">
            <Plus className="w-6 h-6 mx-auto text-[var(--text-tertiary)]" strokeWidth={1.5} />
            <div className="mt-2 text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.01em]">Drop files to attach</div>
            <div className="text-[11.5px] text-[var(--text-tertiary)] mt-0.5">PDF, images, CSV, docs…</div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="h-14 shrink-0 flex items-center gap-2 md:gap-2.5 px-3 md:px-5 border-b hairline min-w-0">
        {agent.definable ? <Logo size={20} className="shrink-0 text-[var(--text-primary)]" /> : <PixelAvatar
          seed={agent.seed}
          hue={agent.hue}
          size={22}
          ring
          rounded={6}
          animate={source.live ? "run" : "alive"}
          emotion={source.live ? "happy" : agentMood(agent)}
        />}
        <div className="flex flex-1 items-center gap-2.5 min-w-0">
          {/* Below desktop an open chat shows only its title: the avatar already names the agent */}
          <span className={`${chat ? "hidden lg:block" : ""} text-[13.5px] font-medium text-[var(--text-primary)] truncate`}>{agent.name}</span>
          {chat && (
            <>
              <span className="hidden lg:inline text-[var(--text-quaternary)] text-[13.5px] shrink-0">/</span>
              {renamingTitle ? (
                <RenameInput
                  value={chat.title}
                  onSubmit={(t) => { onRenameChat(t); setRenamingTitle(false); }}
                  onCancel={() => setRenamingTitle(false)}
                  className="w-[220px] min-w-0 h-[24px] px-1.5 rounded-[6px] text-[13px] tracking-[-0.005em] bg-[var(--surface)] text-[var(--text-primary)] ring-1 ring-[var(--hairline-strong)] outline-none"
                />
              ) : (
                <ChatMenu
                  agent={agent}
                  chat={chat}
                  onRename={() => setRenamingTitle(true)}
                  onTogglePin={onTogglePinChat}
                  onBranch={onBranchChat}
                  onArchive={onArchiveChat}
                  onDelete={onDeleteChat}
                  trigger={
                    <button
                      onDoubleClick={() => setRenamingTitle(true)}
                      title="Chat options"
                      className="press group flex items-center gap-1 min-w-0 rounded-[6px] px-1 -mx-1 hover:bg-[var(--accent-hover)] data-[state=open]:bg-[var(--accent-hover)]"
                    >
                      {chat.pinned && <ChatPin pop className="shrink-0 text-[var(--warn)]" />}
                      {chat.branchedFrom && <GitBranch className="w-3 h-3 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} title={`Branched from ${chat.branchedFrom.title}`} />}
                      <span title={chat.title} className="text-[13.5px] text-[var(--text-secondary)] truncate min-w-0">{chat.title}</span>
                      <ChevronDown className="w-3 h-3 shrink-0 text-[var(--text-quaternary)] group-hover:text-[var(--text-tertiary)]" strokeWidth={2} />
                    </button>
                  }
                />
              )}
              {chat.via && catalogFor(chat.via) && (
                <span
                  title={`This chat came in via ${catalogFor(chat.via)!.name}`}
                  className="shrink-0 hidden sm:inline-flex items-center gap-1 rounded-full text-[10.5px] font-medium px-2 py-[2px] bg-[var(--surface-2)] text-[var(--text-secondary)] ring-1 ring-[var(--hairline-strong)]"
                >
                  <ViaIcon type={chat.via} size={10} />
                  via {catalogFor(chat.via)!.name}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {history && <button
            type="button"
            title="Chats"
            aria-expanded={historyOpen && !panel}
            aria-controls="agent-chat-history"
            onClick={() => { setPanel(null); onHistoryOpenChange?.(panel ? true : !historyOpen); }}
            className={`press h-8 px-2.5 shrink-0 rounded-[9px] flex items-center gap-1.5 text-[12.5px] hover:bg-[var(--accent-hover)] ${historyOpen && !panel ? "bg-[var(--accent-active)] text-[var(--text-primary)]" : "text-[var(--text-secondary)]"}`}
          ><PanelRight className="w-3.5 h-3.5" strokeWidth={1.75} />Chats</button>}
        </div>
      </header>

      {/* Body row: chat column + optional right panel */}
      <div className="flex-1 min-h-0 flex min-w-0">
        <div className="flex-1 min-w-0 flex flex-col">
          {/* Empty: scrolls on a short screen (a phone on its side, 200% zoom). my-auto centres it when it
              fits; justify-center would push the overflow past the top, where no scroll can reach it. */}
          {isEmpty ? (
            <div className="flex-1 min-h-0 flex flex-col items-center px-6 overflow-y-auto">
              <div className="relative isolate w-full max-w-[680px] my-auto py-6 flex flex-col items-center text-center fade-in-up">
                <HeroGlow />
                {agent.definable
                  ? <Logo size={48} className="text-[var(--text-primary)]" />
                  : <PixelAvatar seed={agent.seed} hue={agent.hue} size={56} ring rounded={14} emotion={agentMood(agent)} />}
                <h1 className="mt-5 text-[28px] leading-[34px] font-medium tracking-[-0.025em] text-[var(--text-primary)]">
                  {agent.definable ? "What are we building?" : "Where should we begin?"}
                </h1>
                {agent.definable && (
                  <p className="mt-2 max-w-[440px] text-[13px] leading-[1.5] text-[var(--text-tertiary)]">
                    Builder builds, tests and fixes your agents with you. It changes drafts only: you publish.
                  </p>
                )}
                <div className="w-full mt-7 text-left">
                  {composerNote}
                  <Composer
                    onSend={send}
                    agent={agent}
                    autoFocus={!!startWith?.draft}
                    {...composerExtras}
                  />
                </div>
                <div className="mt-4 flex flex-wrap justify-center gap-2">
                  {starters.map((s, i) => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={i}
                        onClick={() => send(s.text, [], false)}
                        className="press h-8 max-w-full px-3 rounded-full flex items-center gap-2 bg-[var(--surface-2)] hover:bg-[var(--surface-3)] text-[12px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] min-w-0"
                      >
                        <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.75} />
                        <span className="truncate">{s.text}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <MessageList
              agent={agent}
              messages={messages}
              older={source.older}
              onOpenArtifact={openArtifact}
              activeArtifactId={panel?.artifact.id}
              onRetry={channel ? undefined : source.retry}
              // a resend while a run is live would join its queue, and while it waits it would be the answer
              onEditMessage={channel || source.live || source.paused ? undefined : source.edit}
              onQuote={channel ? undefined : draft.setQuote}
              turn={source.turn}
              onRegenerate={channel ? undefined : source.regenerate}
              regenerable={source.regenerable}
              onVote={source.vote}
              branchedFrom={source.branchedFrom ?? chat?.branchedFrom}
              summaryEndId={source.summaryEndId}
              onOpenSource={() => onOpenChat?.(chat!.branchedFrom!.id)}
              footer={source.stopped && !channel ? <RunStopped onContinue={source.resume} /> : null}
            />
          )}

          {/* Composer area + the paused run's dock (skip for empty state which has its own composer) */}
          {!isEmpty && (
            <div className="shrink-0 px-3 md:px-4 pt-2 pb-[max(env(safe-area-inset-bottom),12px)] md:pb-5 bg-gradient-to-t from-[var(--canvas)] via-[var(--canvas)] to-transparent">
              <div className="relative max-w-[720px] mx-auto">
                {!channel && composerNote}
                <ComposerDock
                  key={chat?.id}
                  agentName={agent.name}
                  /* A channel's run is answered on the channel, by the person it asked. */
                  paused={channel ? null : source.paused}
                  onAnswer={source.answer}
                  onStop={source.stop}
                />
                <div className="relative z-[1]">
                  {channel ? (
                    <ChannelNote via={channel} agentName={agent.name} />
                  ) : (
                    <Composer
                      onSend={send}
                      agent={agent}
                      live={source.live}
                      onStop={source.stop}
                      {...composerExtras}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        {history && !isDesktop && (
          <BottomSheet open={historyOpen && !panel} onOpenChange={onHistoryOpenChange} title={<span className="sr-only">Chat history</span>}>
            <div id="agent-chat-history" className="h-full">{history}</div>
          </BottomSheet>
        )}
        {panel && <ArtifactViewer artifact={panel.artifact} onClose={() => setPanel(null)} />}
      </div>
    </main>
    {history && isDesktop && (
      <aside id="agent-chat-history" aria-label="Agent chats" className={historyOpen && !panel ? "w-80 shrink-0 min-h-0 flex flex-col rounded-[18px] overflow-hidden ring-1 ring-[var(--hairline)] shadow-lift slide-in-right" : "hidden"}>
        {history}
      </aside>
    )}
    </div>
  );
}
